import { ClerkLoaded, ClerkLoading, ClerkProvider, SignInButton, UserButton, useAuth } from "@clerk/react";
import { StrictMode, useEffect, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import type { ReactNode } from "react";
import { analyticsCacheKey, loadDashboardData, loadFunnelAnalytics, shouldLoadFunnelAnalytics, type DashboardData } from "./api";
import {
  buildConsoleViewModel,
  formatMoney,
  type AnalyticsViewModel,
  type ConsoleViewModel,
  type FunnelRowModel
} from "./view-model";
import "./styles.css";

declare const __FOS_CONSOLE_MOCK_AUTH__: boolean;

const clerkPublishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string | undefined;
const allowMockAuth = import.meta.env.DEV && __FOS_CONSOLE_MOCK_AUTH__;
const nav = ["Overview", "Funnels", "AgentView", "Analytics", "Payments", "Contacts"] as const;
type ViewId = (typeof nav)[number];

type LoadState =
  | { status: "loading" }
  | { status: "ready"; data: DashboardData }
  | { status: "error"; message: string };

function RootApp() {
  if (clerkPublishableKey) {
    return (
      <ClerkProvider publishableKey={clerkPublishableKey}>
        <ClerkLoading>
          <Frame active="Overview" title="Dashboard"><LoadingView label="Loading sign-in" /></Frame>
        </ClerkLoading>
        <ClerkLoaded>
          <ClerkGate />
        </ClerkLoaded>
      </ClerkProvider>
    );
  }

  return allowMockAuth ? <App /> : <MockPreview />;
}

function ClerkGate() {
  const { isSignedIn } = useAuth();
  return isSignedIn ? <App /> : <Frame active="Overview" title="Dashboard"><SignInView /></Frame>;
}

function App() {
  const [state, setState] = useState<LoadState>({ status: "loading" });

  useEffect(() => {
    let alive = true;
    loadDashboardData()
      .then((data) => {
        if (alive) setState({ status: "ready", data });
      })
      .catch((error: unknown) => {
        if (alive) setState({ status: "error", message: error instanceof Error ? error.message : "Unable to load dashboard" });
      });
    return () => {
      alive = false;
    };
  }, []);

  if (state.status === "loading") {
    return <Frame active="Overview" title="Dashboard"><LoadingView label="Loading dashboard" /></Frame>;
  }
  if (state.status === "error") {
    return <Frame active="Overview" title="Dashboard"><ErrorView message={state.message} /></Frame>;
  }

  return <Dashboard data={state.data} />;
}

function Dashboard({ data, allowAnalyticsRequests = true }: { data: DashboardData; allowAnalyticsRequests?: boolean }) {
  const [active, setActive] = useState<ViewId>("Overview");
  const [selectedFunnel, setSelectedFunnel] = useState(data.funnels[0]?.funnelId ?? "");
  const [includeSynthetic, setIncludeSynthetic] = useState(false);
  const [analyticsByFunnel, setAnalyticsByFunnel] = useState(data.analyticsByFunnel);
  const model = useMemo(() => buildConsoleViewModel({ ...data, analyticsByFunnel }), [data, analyticsByFunnel]);
  const selectedAnalytics = model.analytics.find(
    (item) => item.funnelId === selectedFunnel && item.includeSynthetic === includeSynthetic
  );

  useEffect(() => {
    if (!shouldLoadFunnelAnalytics({ analyticsByFunnel, selectedFunnel, includeSynthetic, allowRequests: allowAnalyticsRequests })) {
      return;
    }
    const key = analyticsCacheKey(selectedFunnel, includeSynthetic);
    let alive = true;
    loadFunnelAnalytics(selectedFunnel, includeSynthetic)
      .then((analytics) => {
        if (!alive) return;
        setAnalyticsByFunnel((previous) => new Map(previous).set(key, analytics));
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [allowAnalyticsRequests, analyticsByFunnel, selectedFunnel, includeSynthetic]);

  return (
    <Frame active={active} title={active} onNav={setActive} accountId={model.accountId}>
      {active === "Overview" ? <OverviewView model={model} onNav={setActive} /> : null}
      {active === "Funnels" ? <FunnelsView model={model} /> : null}
      {active === "AgentView" ? <AgentView model={model} selectedFunnel={selectedFunnel} onSelectFunnel={setSelectedFunnel} /> : null}
      {active === "Analytics" ? (
        <AnalyticsView
          model={model}
          analytics={selectedAnalytics}
          selectedFunnel={selectedFunnel}
          includeSynthetic={includeSynthetic}
          onSelectFunnel={setSelectedFunnel}
          onSynthetic={setIncludeSynthetic}
        />
      ) : null}
      {active === "Payments" ? <PaymentsView model={model} /> : null}
      {active === "Contacts" ? <ContactsView model={model} /> : null}
    </Frame>
  );
}

function Frame({
  active,
  title,
  children,
  onNav,
  accountId
}: {
  active: ViewId;
  title: string;
  children: ReactNode;
  onNav?: (view: ViewId) => void;
  accountId?: string;
}) {
  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="brand"><span className="brand-mark">f</span><span>funlo</span></div>
        <nav className="nav" aria-label="Dashboard views">
          {nav.map((item) => (
            <button key={item} type="button" className={`nav-item ${active === item ? "active" : ""}`} onClick={() => onNav?.(item)}>
              <span>{item}</span>
            </button>
          ))}
        </nav>
        <div className="sidebar-note"><span>Test mode</span><strong>No real money</strong></div>
      </aside>
      <main className="content" aria-label={title}>
        <header className="topbar">
          <button className="search-box" type="button" aria-label="Command menu placeholder">
            <span>Search funnels, contacts, payments</span><kbd>⌘K</kbd>
          </button>
          <div className="account-pill">
            <span className="dot" />
            <span>{accountId ?? "dashboard"}</span>
            {clerkPublishableKey ? <UserButton /> : null}
          </div>
        </header>
        {children}
      </main>
    </div>
  );
}

function OverviewView({ model, onNav }: { model: ConsoleViewModel; onNav: (view: ViewId) => void }) {
  const cards = [
    { label: "Live funnels", value: String(model.summary.liveFunnels), tone: "blue" },
    { label: "Real contacts", value: String(model.summary.leadCount), tone: "mint" },
    { label: model.summary.revenuePartial ? "Loaded real revenue" : "Real revenue", value: model.summary.revenueCents ? formatMoney(model.summary.revenueCents, "USD") : "--", tone: "violet" },
    { label: "Split tests", value: model.summary.splitTestsRunning ? String(model.summary.splitTestsRunning) : "--", tone: "peach" }
  ];
  const firstAnalytics = model.analytics[0];

  return (
    <div className="view-stack">
      <PageHead title="Overview" sub="Real totals only. Synthetic test traffic is shown in Analytics when selected." />
      <section className="summary-grid">
        {cards.map((card) => <StatCard key={card.label} {...card} />)}
      </section>
      {model.firstRun ? <FirstRunCard funnelId={model.funnels[0]?.funnelId} /> : null}
      <div className="dashboard-grid">
        <section className="panel panel-wide">
          <PanelHead title="Funnel performance" detail={`${model.funnels.length} total`} />
          <FunnelTable funnels={model.funnels} />
        </section>
        <section className="panel">
          <PanelHead title="Activity feed" detail="AgentView mirror" />
          <EmptyState title="No feed yet" body="Agent activity appears in AgentView for the selected funnel." />
        </section>
      </div>
      <section className="panel">
        <PanelHead title="Revenue trend" detail={firstAnalytics?.includeSynthetic ? "Synthetic" : "Real"} />
        {firstAnalytics && !firstAnalytics.empty ? <MiniChart analytics={firstAnalytics} /> : <EmptyState title="--" body="Run test traffic or receive real events to populate this chart." />}
      </section>
      <button className="secondary-button" type="button" onClick={() => onNav("Funnels")}>View funnels</button>
    </div>
  );
}

function FunnelsView({ model }: { model: ConsoleViewModel }) {
  return (
    <div className="view-stack">
      <PageHead title="Funnels" sub="Preview and live links for account-scoped funnels." />
      <section className="panel panel-wide"><FunnelTable funnels={model.funnels} /></section>
    </div>
  );
}

function AnalyticsView({
  model,
  analytics,
  selectedFunnel,
  includeSynthetic,
  onSelectFunnel,
  onSynthetic
}: {
  model: ConsoleViewModel;
  analytics?: AnalyticsViewModel;
  selectedFunnel: string;
  includeSynthetic: boolean;
  onSelectFunnel: (funnelId: string) => void;
  onSynthetic: (value: boolean) => void;
}) {
  return (
    <div className="view-stack">
      <PageHead title="Analytics" sub={includeSynthetic ? "Synthetic test traffic only. Not blended into Overview totals." : "Real traffic only."} />
      <div className="toolbar">
        <select value={selectedFunnel} onChange={(event) => onSelectFunnel(event.target.value)} aria-label="Funnel">
          {model.funnels.map((funnel) => <option key={funnel.funnelId} value={funnel.funnelId}>{funnel.label}</option>)}
        </select>
        <label className="switch"><input type="checkbox" checked={includeSynthetic} onChange={(event) => onSynthetic(event.target.checked)} /> Show synthetic</label>
      </div>
      {!analytics || analytics.empty ? <EmptyState title="No traffic yet" body="Run test traffic to populate drop-off, revenue, attribution, and split comparison." /> : <AnalyticsCards analytics={analytics} />}
    </div>
  );
}

function ContactsView({ model }: { model: ConsoleViewModel }) {
  const [filter, setFilter] = useState("all");
  const contacts = filter === "all" ? model.contacts : model.contacts.filter((contact) => contact.funnelId === filter);
  return (
    <div className="view-stack">
      <PageHead title="Contacts" sub="Leads with first-touch source from the opt-in payload." />
      <div className="toolbar">
        <select value={filter} onChange={(event) => setFilter(event.target.value)} aria-label="Contact funnel filter">
          <option value="all">All funnels</option>
          {model.funnels.map((funnel) => <option key={funnel.funnelId} value={funnel.funnelId}>{funnel.label}</option>)}
        </select>
      </div>
      <section className="panel panel-wide">
        {contacts.length === 0 ? <EmptyState title="No contacts yet" body="New opt-ins appear here after visitors submit a funnel form." /> : (
          <div className="table contacts-table">
            <div className="table-row table-head"><span>Name</span><span>Email</span><span>Source</span><span>Funnel</span><span>Opted in</span></div>
            {contacts.map((contact) => (
              <div className="table-row" key={contact.id}>
                <strong>{contact.displayName}</strong><span>{contact.email}</span><span>{contact.sourceLabel || "Direct"}</span><span>{contact.funnelId}</span><span>{contact.optedIn ? "Yes" : "No"}</span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function PaymentsView({ model }: { model: ConsoleViewModel }) {
  const upsells = model.payments.filter((payment) => payment.step === "upsell");
  const takeRate = model.payments.length === 0 ? "--" : `${Math.round((upsells.length / model.payments.length) * 100)}%`;
  return (
    <div className="view-stack">
      <PageHead title="Payments" sub="Read-only sandbox payment attempts. No production charges are enabled." />
      <div className="alert">Test mode. Every row is sandbox-only and marked [TEST]. Revenue totals stay out of this view.</div>
      <section className="summary-grid compact"><StatCard label="Upsell take-rate" value={takeRate} tone="blue" /><StatCard label="Payment rows" value={String(model.payments.length)} tone="mint" /></section>
      <section className="panel panel-wide">
        {model.payments.length === 0 ? <EmptyState title="No payments yet" body="Run test checkout to populate sandbox payment attempts." /> : (
          <div className="table payments-table">
            <div className="table-row table-head"><span>Mode</span><span>Funnel</span><span>Step</span><span>Status</span><span>Amount</span></div>
            {model.payments.map((payment) => (
              <div className="table-row" key={payment.id}><strong>{payment.testLabel}</strong><span>{payment.funnelId}</span><span>{payment.step}</span><span>{payment.status}</span><span>{payment.amountLabel}</span></div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function AgentView({ model, selectedFunnel, onSelectFunnel }: { model: ConsoleViewModel; selectedFunnel: string; onSelectFunnel: (value: string) => void }) {
  const funnel = model.funnels.find((item) => item.funnelId === selectedFunnel) ?? model.funnels[0];
  const events = funnel ? model.agentEvents.filter((event) => !event.funnelId || event.funnelId === funnel.funnelId) : model.agentEvents;
  return (
    <div className="agent-layout">
      <section className="panel">
        <PanelHead title="AgentView" detail="Read-only mirror" />
        <div className="toolbar inset">
          <select value={funnel?.funnelId ?? ""} onChange={(event) => onSelectFunnel(event.target.value)} aria-label="AgentView funnel">
            {model.funnels.map((item) => <option key={item.funnelId} value={item.funnelId}>{item.label}</option>)}
          </select>
        </div>
        <div className="event-list">
          {events.length === 0 ? <EmptyState title="No agent activity" body="Operator events for this account will appear here." /> : events.map((event) => (
            <article className="event-row" key={event.id}><strong>{event.message}</strong><span>{event.type} · {formatDate(event.createdAt)}</span></article>
          ))}
        </div>
        <form className="composer" onSubmit={(event) => event.preventDefault()} aria-disabled="true">
          <input disabled placeholder="Commands run from chat or CLI" />
          <button disabled type="submit">Send</button>
        </form>
      </section>
      <section className="panel preview-panel">
        <PanelHead title="Funnel preview" detail={funnel?.label ?? "No funnel"} />
        {funnel?.previewUrl ? <iframe title="Funnel preview" src={funnel.previewUrl} sandbox="allow-scripts allow-same-origin" /> : <EmptyState title="No preview yet" body="Deploy a funnel to enable a safe iframe preview." />}
      </section>
    </div>
  );
}

function FunnelTable({ funnels }: { funnels: FunnelRowModel[] }) {
  if (funnels.length === 0) return <EmptyState title="No funnels yet" body="Create your first funnel from chat or CLI, then preview and live links appear here." />;
  return (
    <div className="table funnel-table">
      <div className="table-row table-head"><span>Funnel</span><span>Status</span><span>CVR</span><span>Revenue</span><span>Links</span></div>
      {funnels.map((funnel) => (
        <div className="table-row" key={funnel.funnelId}>
          <span><strong>{funnel.label}</strong><small>{funnel.funnelId}</small></span>
          <span><Badge status={funnel.status} label={funnel.displayStatus} /></span>
          <span>{funnel.leadCount ? `${funnel.leadCount} leads` : "--"}</span>
          <span>{funnel.revenueLabel}</span>
          <span className="link-pair">{funnel.previewUrl ? <a href={funnel.previewUrl}>Preview</a> : <span className="muted">Preview</span>}{funnel.liveUrl ? <a href={funnel.liveUrl}>Live</a> : <span className="muted">Live</span>}</span>
        </div>
      ))}
    </div>
  );
}

function AnalyticsCards({ analytics }: { analytics: AnalyticsViewModel }) {
  return (
    <section className="analytics-grid">
      <div className="metric-block"><span>{analytics.includeSynthetic ? "Synthetic revenue" : "Real revenue"}</span><strong>{analytics.revenueLabel}</strong></div>
      <div className="dropoff"><h3>Drop-off</h3>{analytics.dropOff.map((step) => <BarRow key={step.stepId} label={step.stepId} value={step.completionRate} />)}</div>
      <div className="variants"><h3>Split comparison</h3>{analytics.variants.length ? analytics.variants.map((variant) => <article className="variant-row" key={`${variant.experimentId}:${variant.variantId}`}><span>{variant.variantId}</span><strong>{variant.revenueLabel}</strong><small>{variant.visitors} visitors · {variant.checkoutClicks} checkout clicks</small></article>) : <p className="muted">No split-test traffic yet.</p>}</div>
      <div className="attribution"><h3>Sources</h3>{analytics.attribution.map((row) => <div className="source-row" key={row.source}><span>{row.source}</span><strong>{row.leads}</strong></div>)}</div>
    </section>
  );
}

function MiniChart({ analytics }: { analytics: AnalyticsViewModel }) {
  const total = analytics.revenue.reduce((sum, row) => sum + row.revenueAmount, 0);
  return <div className="mini-chart"><strong>{total ? formatMoney(total, analytics.revenue[0]?.revenueCurrency ?? "USD") : "--"}</strong><span>{analytics.includeSynthetic ? "Synthetic series" : "Real series"}</span></div>;
}

function StatCard({ label, value, tone }: { label: string; value: string; tone: string }) {
  return <div className="stat-card"><span className={`stat-chip ${tone}`} /><p>{label}</p><strong>{value}</strong></div>;
}

function FirstRunCard({ funnelId }: { funnelId?: string }) {
  const [copiedCommand, setCopiedCommand] = useState<string | null>(null);
  const marketplaceCommand = "claude plugin marketplace add plugins/.claude-plugin/marketplace.json";
  const installCommand = "claude plugin install funlo";
  const createCommand = `funnel create-funnel --funnel-id ${funnelId ?? "<funnel-id>"} --spec <path>`;
  const testCheckoutCommand = funnelId
    ? `funnel test-checkout --funnel-id ${funnelId} --offer-id <offer-id> --json`
    : "funnel test-checkout --funnel-id <funnel-id> --offer-id <offer-id> --json";
  const rollbackCommand = funnelId
    ? `funnel rollback --funnel-id ${funnelId} --version-id <version-id>`
    : "funnel rollback --funnel-id <funnel-id> --version-id <version-id>";
  const copyCommand = (label: string, command: string) => {
    void navigator.clipboard?.writeText(command);
    setCopiedCommand(label);
  };

  return (
    <section className="first-run command-card">
      <div>
        <p className="eyebrow">Private pilot setup</p>
        <h2>Install the local Claude plugin first.</h2>
        <p className="setup-copy">M1.6 uses a private local Claude plugin. Hosted ChatGPT OAuth and public marketplace distribution stay in M2.</p>
        <code>{marketplaceCommand}</code>
        <code>{installCommand}</code>
        <div className="safety-actions compact-actions">
          <button type="button" onClick={() => copyCommand("marketplace", marketplaceCommand)}>Copy marketplace command</button>
          <button type="button" onClick={() => copyCommand("install", installCommand)}>Copy install command</button>
        </div>
      </div>
      <div>
        <p className="eyebrow">First funnel</p>
        <h2>Your account is ready for its first funnel.</h2>
        <p className="setup-copy">After the plugin loads, ask Claude to use the funlo build skill, then run the create command.</p>
        <code>{createCommand}</code>
      </div>
      <div className="safety-actions">
        <button type="button" onClick={() => copyCommand("test checkout", testCheckoutCommand)}>Copy test-checkout command</button>
        <button type="button" onClick={() => copyCommand("rollback", rollbackCommand)}>Copy rollback command</button>
        <span>{copiedCommand ? `Copied ${copiedCommand} command.` : "Expired tokens show a sign-in or re-provision recovery path."}</span>
      </div>
    </section>
  );
}

function BarRow({ label, value }: { label: string; value: number }) {
  return <div className="bar-row"><span>{label}</span><div className="bar-track"><span style={{ width: `${Math.round(value * 100)}%` }} /></div><strong>{Math.round(value * 100)}%</strong></div>;
}

function PanelHead({ title, detail }: { title: string; detail: string }) {
  return <header className="panel-head"><h2>{title}</h2><span>{detail}</span></header>;
}

function PageHead({ title, sub }: { title: string; sub: string }) {
  return <div className="page-head"><div><h1>{title}</h1><p>{sub}</p></div></div>;
}

function Badge({ status, label }: { status: string; label: string }) {
  return <span className={`badge ${status}`}>{label}</span>;
}

function LoadingView({ label }: { label: string }) {
  return <div className="center-state"><span className="loader" /><strong>{label}</strong></div>;
}

function SignInView() {
  return (
    <div className="center-state auth-state">
      <strong>Sign in to funlo</strong>
      <p>Use your invited account to view funnels, contacts, payments, and test-mode analytics.</p>
      <SignInButton mode="modal"><button type="button" className="primary-button">Sign in</button></SignInButton>
    </div>
  );
}

function ErrorView({ message }: { message: string }) {
  const recoverableSession = message.includes("401");
  return (
    <div className="center-state">
      <strong>{recoverableSession ? "Session expired" : "Dashboard unavailable"}</strong>
      <p>{recoverableSession ? "Sign in again to recover your dashboard session." : message}</p>
      {recoverableSession ? <SignInButton mode="modal"><button type="button" className="primary-button">Sign in again</button></SignInButton> : null}
    </div>
  );
}

function EmptyState({ title, body }: { title: string; body: string }) {
  return <div className="empty-state"><strong>{title}</strong><span>{body}</span></div>;
}

function MockPreview() {
  const data = useMemo<DashboardData>(() => ({
    accountId: "mock_preview",
    funnels: [{ accountId: "mock_preview", funnelId: "fun-preview", displayName: "Mock preview funnel", createdAt: "2026-06-28T00:00:00.000Z", status: "live", previewUrl: "https://preview.example", liveUrl: "https://preview.example" }],
    contacts: [],
    payments: [],
    agentEvents: [],
    analyticsByFunnel: new Map([
      [analyticsCacheKey("fun-preview", false), emptyMockAnalytics(false)],
      [analyticsCacheKey("fun-preview", true), emptyMockAnalytics(true)]
    ])
  }), []);
  return <><div className="mock-banner">Mock-only preview. Sign-in is not configured and no dashboard API calls are made.</div><Dashboard data={data} allowAnalyticsRequests={false} /></>;
}

function emptyMockAnalytics(includeSynthetic: boolean) {
  return {
    accountId: "mock_preview",
    funnelId: "fun-preview",
    includeSynthetic,
    steps: [],
    revenue: [],
    attribution: [],
    variants: []
  };
}

function formatDate(value: string): string {
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }).format(new Date(value));
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <RootApp />
  </StrictMode>
);
